import { Input, TextInput } from "@narmi/design_system";
import { maskitoDateOptionsGenerator } from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import React, { useCallback, useImperativeHandle, useRef, useState } from "react";

const dateOptions = maskitoDateOptionsGenerator({ mode: "mm/dd/yyyy", separator: "/" });

// server value is YYYY-MM-DD
// client display value is MM/DD/YYYY
export const getDisplayValue = (storedValue: string) => {
  if (!storedValue) return "";
  const dateAsArr = storedValue.split("-");
  const year = dateAsArr[0] || "";
  const cutOffYear = year.length > 4;
  const yearDisplay = cutOffYear ? `${year.substring(0, 4)}` : year;

  const month = dateAsArr[1] || "";
  const showMonthSlash = month.length > 1;
  const monthDisplay = showMonthSlash ? `${month}/` : month;

  const day = dateAsArr[2] || "";
  const showDaySlash = day.length > 1;
  const dayDisplay = showDaySlash ? `${day}/` : day;

  return `${monthDisplay}${dayDisplay}${yearDisplay}`;
};

export const getStoredValue = (displayValue: string) => {
  if (!displayValue) return "";
  const dateAsArr = displayValue.split("/");
  const numSegments =
    dateAsArr[dateAsArr.length - 1] === "" ? dateAsArr.length - 1 : dateAsArr.length;
  let shouldZeroPrefix = false;
  if (numSegments === 1) {
    if (displayValue.slice(-1) === "/" && dateAsArr[0].length < 2) {
      shouldZeroPrefix = true;
    }
    return `-${shouldZeroPrefix ? "0" : ""}${dateAsArr[0]}-`;
  } else if (numSegments === 2) {
    if (displayValue.slice(-1) === "/" && dateAsArr[1].length < 2) {
      shouldZeroPrefix = true;
    }
    return `-${dateAsArr[0]}-${shouldZeroPrefix ? "0" : ""}${dateAsArr[1]}`;
  } else {
    return `${dateAsArr[2]}-${dateAsArr[0]}-${dateAsArr[1]}`;
  }
};

export function normalizeDate(date: string | null | undefined) {
  if (!date) {
    return date;
  }

  const segments = date.split("/").filter(Boolean).length;
  if (segments === 3) {
    return getStoredValue(date);
  }

  if (segments === 2) {
    const [month, year] = date.split("/");
    return `${year}-${month}-01`;
  }

  return date;
}

/**
 * Reformats a backend DRF date format validation error message to display
 * the user-facing input format (e.g. "MM/DD/YYYY") instead of the internal
 * ISO format.
 */
export function formatDateFieldError(
  error: string | undefined,
  displayFormat: string
): string | undefined {
  if (!error) {
    return error;
  }

  if (error.includes("Date has wrong format. Use one of these formats instead: YYYY-MM-DD.")) {
    return `Date has wrong format. Use this format instead: ${displayFormat}.`;
  }

  return error;
}

/**
 * Walks an application object and converts any slash-delimited date fields
 * (date_of_birth, date_of_incorporation) to ISO 8601 format before submission.
 */
export function normalizeApplicationDates<
  T extends {
    applicants?: Array<{ date_of_birth?: string | null }>;
    organization?: { date_of_incorporation?: string | null } | null;
  },
>(application: T): T {
  return {
    ...application,
    applicants: application.applicants?.map((applicant) => ({
      ...applicant,
      date_of_birth: applicant.date_of_birth
        ? normalizeDate(applicant.date_of_birth)
        : applicant.date_of_birth,
    })),
    organization: application.organization
      ? {
          ...application.organization,
          date_of_incorporation: application.organization.date_of_incorporation
            ? normalizeDate(application.organization.date_of_incorporation)
            : application.organization.date_of_incorporation,
        }
      : application.organization,
  };
}

export const MaskedSimpleDateInput = ({
  isInitiallyMasked,
  label,
  onChange,
  onBlur,
  value,
  error,
}: {
  isInitiallyMasked: boolean;
  label: string;
  onChange: (value: string) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
  error: string;
}) => {
  const [showMask, setShowMask] = React.useState(isInitiallyMasked);
  const ref = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isInitiallyMasked && !showMask) {
      ref.current?.focus();
    }
  }, [isInitiallyMasked, showMask]);

  if (showMask) {
    return (
      <TextInput value="**/**/****" label={label} readOnly onFocus={() => setShowMask(false)} />
    );
  }
  return (
    <SimpleDateInput
      ref={ref}
      label={label}
      onChange={onChange}
      onBlur={onBlur}
      value={value}
      error={error}
    />
  );
};

interface SimpleDateInputProps {
  field?: string; // for compatibility with ContextForm
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  name?: string;
  id?: string;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  placeholder?: string;
}
/**
 *
 * NDS TextInput configured for entering a date.
 *
 * `SimpleDateInput` is intended to be used as a controlled component.
 *
 * Only numeric characters are permitted.
 *
 * Use this instead of the NDS `DateInput` when the date picker is not desired.
 *
 */
const SimpleDateInput = React.forwardRef<{ focus: () => void }, SimpleDateInputProps>(
  (
    {
      label,
      value,
      onChange,
      error = null,
      name = "date_input",
      id = "date_input",
      disabled,
      placeholder = "MM/DD/YYYY",
      ...otherTextInputProps
    },
    parentRef
  ) => {
    const inputEl = useRef<HTMLInputElement | null>(null);
    const maskitoRef = useMaskito({ options: dateOptions });
    // Maskito mutates the DOM directly rather than going through React's
    // controlled-input reconciliation. We track the display value in local
    // state so React doesn't reset the field after every keystroke.
    const [displayValue, setDisplayValue] = useState(getDisplayValue(value ?? ""));

    // Sync display value when the stored value changes externally
    // (e.g., form reset, async hydration).
    React.useEffect(() => {
      const expected = getDisplayValue(value ?? "");
      if (expected !== displayValue) {
        setDisplayValue(expected);
      }
    }, [value]);

    const mergedRef = useCallback(
      (el: HTMLInputElement | null) => {
        inputEl.current = el;
        maskitoRef(el);
      },
      [maskitoRef]
    );

    useImperativeHandle(parentRef, () => {
      return {
        focus() {
          inputEl.current?.focus();
        },
      };
    }, []);

    return (
      <Input name={name} id={id} label={label} error={error} disabled={disabled}>
        <input
          ref={mergedRef}
          inputMode="numeric"
          type="text"
          name={name}
          id={id}
          placeholder={label || placeholder}
          aria-label={label || placeholder}
          required
          key="nds-text"
          {...otherTextInputProps}
          value={displayValue}
          onInput={(event) => {
            const val = (event.target as HTMLInputElement).value;
            setDisplayValue(val);
            // only propagate to parent when date is complete, matching cleave behavior
            if (val.length === 10) {
              onChange(getStoredValue(val));
            }
          }}
          onChange={() => {
            // React requires an onChange handler for controlled inputs.
            // Actual value propagation is handled by onInput above.
          }}
        />
      </Input>
    );
  }
);

export default SimpleDateInput;
